import { useEffect } from 'react'
import { SiteNav } from '../components/SiteNav'
import { setPageMeta } from '../utils'

export function PrivacyPage() {
  useEffect(() => {
    setPageMeta({
      title: 'Privacy Policy | arisk.ai',
      description: 'Privacy policy for arisk.ai: how we handle your data, what we collect, and what we do not.',
      canonical: 'https://arisk.ai/privacy',
    })
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900 text-gray-800 dark:text-neutral-200">
      <SiteNav />

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Privacy Policy</h1>
        <p className="text-xs text-gray-400 dark:text-neutral-500 mb-10">Last updated: January 2026</p>

        <div className="space-y-10 text-sm text-gray-600 dark:text-neutral-400 leading-relaxed">

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">The short version</h2>
            <p>
              arisk.ai does not collect, store, or transmit the text you enter. Everything runs in your
              browser. Your data never reaches our servers because we do not have servers involved in
              processing your text.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Your text stays on your device</h2>
            <p>
              All redaction and token replacement happens locally in your browser using JavaScript. The
              text you paste, the words you redact, and the token mappings generated are never sent
              anywhere. You can verify this yourself: open your browser's DevTools, go to the Network
              tab, and use the tool. You will see no outbound requests containing your data.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">What we store in your browser</h2>
            <p>
              We use your browser's localStorage to save two preferences between sessions: your chosen
              theme (light or dark) and any instruction text you have typed into the context field.
              This data stays on your device and is never transmitted to us. You can clear it at any
              time by clearing your browser's site data.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Feedback</h2>
            <p>
              When you submit feedback through the feedback form, we collect your reaction and any
              comment you choose to provide. If you include your email address, we may use it to
              respond. Feedback submissions are handled by Web3Forms and subject to their privacy
              policy. We do not store your feedback data ourselves.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Analytics and tracking</h2>
            <p>
              We do not use Google Analytics or any behavioural tracking tools. We do not use
              third-party advertising scripts. We do not set tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The date at the top of this page reflects
              the most recent revision. Continued use of arisk.ai after changes are posted constitutes
              acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Contact</h2>
            <p>
              Questions about this policy can be sent to{' '}
              <a
                href="mailto:hello@arisk.ai"
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                hello@arisk.ai
              </a>
              {' '}or submitted through the feedback form on the main page.
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}
